import TextField from '@material-ui/core/TextField'
import {onUnknownChangeHandler} from 'lib/dom'
import ColorPicker from 'lib/ui/ColorPicker'
import Select from '@material-ui/core/Select'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Box from '@material-ui/core/Box'
import Slider from 'lib/ui/Slider'
import React from 'react'
import Button from '@material-ui/core/Button'
import styled from 'styled-components'
import ComponentConfig, {
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {useToggle} from 'lib/toggle'
import {Controller, useForm, UseFormMethods} from 'react-hook-form'
import {useTemplate} from 'Event/TemplateProvider'
import {useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {BaseTemplate, Template} from 'Event/template'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'

export default function PostFormStylesConfig() {
  const {flag: showingConfig, toggle: toggleConfig} = useToggle()

  const {postFormStyles} = useTemplate()
  const update = useSaveTemplate()
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: {isDirty},
  } = useForm()

  useAutoUpdate({
    values: {postFormStyles: watch()},
    when: showingConfig,
  })

  const submit = (data: Template['postFormStyles']) => {
    update({
      postFormStyles: data,
    })

    toggleConfig()
  }

  const onClose = () => {
    toggleConfig()
  }

  return (
    <>
      <StyledEditPostFormStylesButton onClick={toggleConfig} />
      <ComponentConfig
        showing={showingConfig}
        onClose={onClose}
        title="Post Form Styles"
        onSubmit={handleSubmit(submit)}
        hasChanges={isDirty}
      >
        <StylingPanel>
          <Styling
            control={control}
            register={register}
            postFormStyles={postFormStyles}
          />
        </StylingPanel>

        <Footer>
          <SaveButton type="submit" />
        </Footer>
      </ComponentConfig>
    </>
  )
}

function EditPostFormStylesButton(props: {
  onClick: () => void
  className?: string
}) {
  return (
    <Button
      className={props.className}
      fullWidth
      size="large"
      variant="contained"
      color="primary"
      aria-label="style post form"
      onClick={props.onClick}
    >
      Edit Post Form Styles
    </Button>
  )
}

function Styling(props: {
  control: UseFormMethods['control']
  register: UseFormMethods['register']
  postFormStyles: BaseTemplate['postFormStyles']
}) {
  const {control, register, postFormStyles} = props

  return (
    <>
      <TextField
        name="width"
        defaultValue={postFormStyles?.width}
        label="Form Width %"
        type="number"
        fullWidth
        inputProps={{
          min: 1,
          max: 100,
          ref: register,
        }}
      />
      <Box mb={2}>
        <InputLabel>Form Position</InputLabel>
        <Controller
          name="position"
          defaultValue={postFormStyles?.position}
          control={control}
          render={({value, onChange}) => (
            <Select
              value={value}
              onChange={onUnknownChangeHandler(onChange)}
              fullWidth
            >
              <MenuItem value="flex-end">Right</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="flex-start">Left</MenuItem>
            </Select>
          )}
        />
      </Box>
      <Controller
        name="inputStyles.labelColor"
        defaultValue={postFormStyles?.inputStyles?.labelColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Label Text Color"
            color={value}
            onPick={onChange}
            aria-label="label text color"
          />
        )}
      />
      <Controller
        name="inputStyles.borderColor"
        defaultValue={postFormStyles?.inputStyles?.borderColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Border Color"
            color={value}
            onPick={onChange}
            aria-label="input box border color"
          />
        )}
      />
      <Controller
        name="inputStyles.backgroundColor"
        defaultValue={postFormStyles?.inputStyles?.backgroundColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Background Color"
            color={value}
            onPick={onChange}
            aria-label="input box background color"
          />
        )}
      />
      <Box>
        <Controller
          name="inputStyles.backgroundOpacity"
          defaultValue={postFormStyles?.inputStyles?.backgroundOpacity}
          control={control}
          render={({value, onChange}) => (
            <Slider
              min={0}
              max={100}
              step={1}
              onChange={onChange}
              valueLabelDisplay="auto"
              value={value}
              label="Input Background Color Opacity"
              unit="%"
            />
          )}
        />
      </Box>
      <Controller
        name="inputStyles.textColor"
        defaultValue={postFormStyles?.inputStyles?.textColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Input Text Color"
            color={value}
            onPick={onChange}
            aria-label="input box text color"
          />
        )}
      />
      <Controller
        name="inputStyles.helperTextColor"
        defaultValue={postFormStyles?.inputStyles?.helperTextColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Helper Text Color"
            color={value}
            onPick={onChange}
            aria-label="helper text color"
          />
        )}
      />
      <TextField
        name="buttonSize"
        defaultValue={postFormStyles?.buttonSize}
        label="Form Button Size %"
        type="number"
        fullWidth
        inputProps={{
          min: 1,
          max: 100,
          ref: register,
        }}
      />
      <TextField
        name="buttonRadius"
        defaultValue={postFormStyles?.buttonRadius}
        label="Form Button Radius"
        type="number"
        fullWidth
        inputProps={{
          min: 0,
          max: 50,
          ref: register,
        }}
      />
      <Controller
        name="buttonColor"
        defaultValue={postFormStyles?.buttonColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Submit Button Text Color"
            color={value}
            onPick={onChange}
            aria-label="submit button color"
          />
        )}
      />
      <Controller
        name="buttonBackgroundColor"
        defaultValue={postFormStyles?.buttonBackgroundColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Submit Button Background Color"
            color={value}
            onPick={onChange}
            aria-label="submit button background color"
          />
        )}
      />
      <Controller
        name="buttonHoverBackgroundColor"
        defaultValue={postFormStyles?.buttonHoverBackgroundColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Submit Button Background Hover Color"
            color={value}
            onPick={onChange}
            aria-label="submit button background hover color"
          />
        )}
      />
      <TextField
        name="buttonFontSize"
        defaultValue={postFormStyles?.buttonFontSize}
        label="Submit Button Font Size"
        type="number"
        fullWidth
        inputProps={{
          min: 0,
          max: 50,
          ref: register,
        }}
      />
      <TextField
        name="buttonBorderWidth"
        defaultValue={postFormStyles?.buttonBorderWidth}
        label="Submit Button Border Width"
        type="number"
        fullWidth
        inputProps={{
          min: 0,
          max: 50,
          ref: register,
        }}
      />
      <Controller
        name="buttonBorderColor"
        defaultValue={postFormStyles?.buttonBorderColor}
        control={control}
        render={({value, onChange}) => (
          <ColorPicker
            label="Submit Button Border Color"
            color={value}
            onPick={onChange}
            aria-label="submit button border color"
          />
        )}
      />
      <Box mb={2}>
        <InputLabel>Submit Button Position</InputLabel>

        <Controller
          name="buttonPosition"
          defaultValue={postFormStyles?.buttonPosition}
          control={control}
          render={({value, onChange}) => (
            <Select
              value={value}
              onChange={onUnknownChangeHandler(onChange)}
              fullWidth
            >
              <MenuItem value="flex-end">Right</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="flex-start">Left</MenuItem>
            </Select>
          )}
        />
      </Box>
    </>
  )
}

const StyledEditPostFormStylesButton = styled(EditPostFormStylesButton)`
  margin-bottom: ${(props) => props.theme.spacing[3]}!important;
`
